// Detect attempts to embed a mobile / phone number inside a free-text
// field. Used on property descriptions, addresses, and amenity entries
// to keep contact info gated through the platform's inquiry flow rather
// than leaking via the listing copy.
//
// Catches the realistic obfuscation patterns:
//   "9876543210"                    raw 10-digit
//   "98765 43210" / "98765-43210"   with separators
//   "+91 9876543210" / "91-987..."  with country code
//   "nine eight seven six..."       fully spelled out
//   "9876 five 4 three 2 1 zero"    mixed digits and words
//
// False-positive avoidance:
//   - "Pin 626123" (6 digits) — NOT flagged (under 10 consecutive)
//   - "GSTIN 33ABCDE1234F1Z5" — NOT flagged (digit runs <10)
//   - "5 rooms"               — NOT flagged
//
// Known holes (intentional for now, can be added later if abused):
//   - Homoglyph attacks like "98765432l0" with lowercase L for 1
//   - Devanagari / Tamil numerals (९८७६५४३२१०)
//   - Email addresses (a different leak vector — handle separately if it shows up)
const NUMBER_WORDS = {
    zero: '0', oh: '0',
    one: '1',
    two: '2',
    three: '3',
    four: '4',
    five: '5',
    six: '6',
    seven: '7',
    eight: '8',
    nine: '9',
    // Common homophones used to evade simple word filters:
    won: '1', to: '2', too: '2', for: '4', fore: '4', ate: '8',
};

// Replace whole-word number names with their digit equivalents.
// Splits on word boundaries so partial words like "thirteen" or
// "fourth" aren't accidentally rewritten — only standalone tokens.
function _replaceNumberWords(text) {
    return text.split(/(\b)/).map(tok => {
        const k = tok.toLowerCase();
        return NUMBER_WORDS[k] !== undefined ? NUMBER_WORDS[k] : tok;
    }).join('');
}

// Strip the separators most commonly used to break up a phone number
// for the eye while leaving the digits readable to a parser.
function _stripPhoneSeparators(text) {
    return text.replace(/[\s\-.()+]/g, '');
}

// Returns true when the input is likely to contain a mobile number.
// The threshold is "any run of 10+ consecutive digits after collapsing
// number-words and common separators". Indian mobile numbers are 10
// digits (or 12 with the 91 country code), so 10 is the natural floor.
export function containsPhoneNumber(text) {
    if (!text) return false;
    const normalized = _stripPhoneSeparators(_replaceNumberWords(String(text)));
    return /\d{10,}/.test(normalized);
}

// Returns the human-readable reason a value was rejected, or null if it
// passes. Lets the calling component flash a message that explains
// what to do instead ("share your number through the inquiry form").
export function phoneFilterReason(text) {
    if (containsPhoneNumber(text)) {
        return "Please don't include phone numbers here — prospects will reach you through the inquiry form.";
    }
    return null;
}
